import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Stack, Typography, Button, TextField, MenuItem, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Pagination, Chip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, FormControlLabel, Checkbox, Autocomplete, Avatar, IconButton, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ImageIcon from '@mui/icons-material/Image';
import PublishIcon from '@mui/icons-material/Publish';
import CloseIcon from '@mui/icons-material/Close';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { fetchAnnouncements, publishAnnouncement as publishAnnouncementAction, deleteAnnouncement as deleteAnnouncementAction } from '../controllers/announcementsController';
import { resolveAnnouncementAssetUrl } from '../api/httpClientAnnouncements';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CreateAnnouncementDialog from '../components/CreateAnnouncementDialog';
import EditAnnouncementDialog from './EditAnnouncementDialog';


const Announcements = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState('');
    const [isPublished, setIsPublished] = useState('');
    const [category, setCategory] = useState('');
    const [createOpen, setCreateOpen] = useState(false);
    const { decoded } = useAuth();
    const [publishingIds, setPublishingIds] = useState(new Set());
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [editOpen, setEditOpen] = useState(false);
    const [selectedAnnouncementId, setSelectedAnnouncementId] = useState(null);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        const datePart = date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
        const timePart = date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
        return `${datePart} - ${timePart}`;
    };

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const params = useMemo(() => ({
        pageNumber: page,
        pageSize,
        isPublished: isPublished === '' ? undefined : (isPublished === 'true'),
        category: category === '' ? undefined : Number(category),
        search: search || undefined,
    }), [page, pageSize, isPublished, category, search]);

    // Check if create query parameter is present
    useEffect(() => {
        if (searchParams.get('create') === 'true') {
            setCreateOpen(true);
            // Remove query parameter from URL
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const handleCreateSuccess = () => {
        // Refresh list
        if (page === 1) {
            // Trigger useEffect by updating params
            setPage(1);
        } else {
            setPage(1);
        }
    };

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError('');
        fetchAnnouncements(params)
            .then(({ data, meta }) => {
                if (!active) return;
                setItems(Array.isArray(data) ? data : []);
                setTotalPages(meta?.totalPages || 1);
            })
            .catch((e) => { if (active) setError(e?.message || 'Duyurular alınamadı'); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [params]);


    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Duyurular</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon sx={{ width: 16 }} />}
                    sx={{
                        textTransform: 'none',
                        backgroundColor: '#E7EDF4',
                        boxShadow: 'none',
                        fontSize: 13,
                        borderRadius: '5px',
                        color: '#0E4D94',
                        '&:hover': {
                            backgroundColor: '#CFDBEA',
                            boxShadow: 'none',
                        },
                    }}
                    onClick={() => setCreateOpen(true)}
                >
                    Yeni Duyuru
                </Button>
            </Stack>
            <Paper variant="outlined" sx={{ border: 'none', boxShadow: 'none', mb: 2, mt: 4, backgroundColor: 'transparent' }}>
                <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <Box sx={{ flex: 1, minWidth: 220 }}>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Ara
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                value={search}
                                onChange={(e) => { setPage(1); setSearch(e.target.value); }}
                                placeholder=""
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '5px',
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#0E4D94',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        py: 1.2,
                                        px: 1.5,
                                        fontSize: '14px',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#e6e6e6',
                                    },
                                }}
                            />
                        </Box>
                        <Box sx={{ minWidth: 180 }}>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Kategori
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                select
                                value={category}
                                onChange={(e) => { setPage(1); setCategory(e.target.value); }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '5px',
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#0E4D94',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        py: 1.2,
                                        px: 1.5,
                                        fontSize: '14px',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#e6e6e6',
                                    },
                                }}
                            >
                                <MenuItem value="">Tümü</MenuItem>
                                <MenuItem value={0}>Genel</MenuItem>
                                <MenuItem value={1}>Etkinlik</MenuItem>
                                <MenuItem value={2}>Proje</MenuItem>
                                <MenuItem value={3}>Vefat</MenuItem>
                                <MenuItem value={4}>Evlilik</MenuItem>
                                <MenuItem value={5}>Doğum</MenuItem>
                                <MenuItem value={6}>Yeni Üye Katılımı</MenuItem>
                                <MenuItem value={7}>Bayram Tebrikleri</MenuItem>
                            </TextField>
                        </Box>
                        <Box sx={{ minWidth: 180 }}>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Durum
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                select
                                value={isPublished}
                                onChange={(e) => { setPage(1); setIsPublished(e.target.value); }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '5px',
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#0E4D94',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        py: 1.2,
                                        px: 1.5,
                                        fontSize: '14px',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#e6e6e6',
                                    },
                                }}
                            >
                                <MenuItem value="">Tümü</MenuItem>
                                <MenuItem value="true">Yayında</MenuItem>
                                <MenuItem value="false">Taslak</MenuItem>
                            </TextField>
                        </Box>
                        <Box sx={{ minWidth: 160 }}>
                            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                                Sayfa Boyutu
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                select
                                value={pageSize}
                                onChange={(e) => { setPage(1); setPageSize(Number(e.target.value)); }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '5px',
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#0E4D94',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        py: 1.2,
                                        px: 1.5,
                                        fontSize: '14px',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#e6e6e6',
                                    },
                                }}
                            >
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={20}>20</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                            </TextField>
                        </Box>
                    </Stack>
                </Stack>
            </Paper>
            {error ? (
                <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>
            ) : (
                <>
                    <TableContainer
                        component={Paper}
                        sx={{
                            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
                            border: '1px solid #f5f5f5',
                            py: 1,
                            px: 2.5
                        }}
                    >
                        <Table size="small">
                            <TableHead sx={{ bgcolor: 'transparent' }}>
                                <TableRow>
                                    <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Görsel</TableCell>
                                    <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Başlık</TableCell>
                                    <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Kategori</TableCell>
                                    <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Otomasyon</TableCell>
                                    <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Durum</TableCell>
                                    <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Yayın Tarihi</TableCell>
                                    <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Oluşturulma Tarihi</TableCell>
                                    <TableCell align="right" sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    Array.from({ length: pageSize }).map((_, index) => (
                                        <TableRow
                                            key={`skeleton-${index}`}
                                            sx={{
                                                '& .MuiTableCell-root': {
                                                    borderBottom: index === pageSize - 1 ? 'none' : '1px dashed #e8e8e8',
                                                    px: 1.5,
                                                    py: 1.5,
                                                },
                                            }}
                                        >
                                            <TableCell>
                                                <Skeleton width={40} height={40} borderRadius={4} />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton width={250} height={20} />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton width={80} height={24} borderRadius={16} />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton width={70} height={20} />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton width={60} height={24} borderRadius={16} />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton width={120} height={20} />
                                            </TableCell>
                                            <TableCell>
                                                <Skeleton width={120} height={20} />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Skeleton width={32} height={32} borderRadius={6} />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    items.map((a, index) => (
                                        <TableRow
                                            key={a.id}
                                            hover
                                            sx={{
                                                '& .MuiTableCell-root': {
                                                    borderBottom: index === items.length - 1 ? 'none' : '1px dashed #e8e8e8',
                                                    px: 1.5,
                                                    py: 1.5,
                                                    color: '#444444',
                                                    fontWeight: 500,
                                                },
                                            }}
                                        >
                                            <TableCell>
                                                {a.coverImageUrl ? (
                                                    <Box
                                                        component="img"
                                                        src={resolveAnnouncementAssetUrl(a.coverImageUrl)}
                                                        alt={a.title}
                                                        sx={{
                                                            width: 50,
                                                            height: 50,
                                                            objectFit: 'cover',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                        }}
                                                        onClick={() => navigate(`/announcements/${a.id}`)}
                                                    />
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            width: 50,
                                                            height: 50,
                                                            borderRadius: '4px',
                                                            backgroundColor: '#f5f5f5',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        <ImageIcon sx={{ fontSize: 24, color: '#a3a3a3' }} />
                                                    </Box>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ cursor: 'pointer' }} onClick={() => navigate(`/announcements/${a.id}`)}>{a.title}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={
                                                        a.category === 1 ? 'Etkinlik' :
                                                            a.category === 2 ? 'Proje' :
                                                                a.category === 3 ? 'Vefat' :
                                                                    a.category === 4 ? 'Evlilik' :
                                                                        a.category === 5 ? 'Doğum' :
                                                                            a.category === 6 ? 'Yeni Üye Katılımı' :
                                                                                a.category === 7 ? 'Bayram Tebrikleri' : 'Genel'
                                                    }
                                                    sx={{
                                                        fontSize: '13px',
                                                        fontWeight: 400,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>{a.automationType === 1 ? 'Tek Sefer' : a.automationType === 2 ? 'Tekrarlayan' : 'Manuel'}</TableCell>
                                            <TableCell>
                                                {a.isPublished ? (
                                                    <Chip
                                                        label="Yayında"
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: '#DFFFEA',
                                                            color: '#17C653',
                                                            fontWeight: 600,
                                                            fontSize: '12px'
                                                        }}
                                                    />
                                                ) : (
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <Chip
                                                            label="Taslak"
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: '#FFEEF3',
                                                                color: '#F8285A',
                                                                fontWeight: 600,
                                                                fontSize: '12px'
                                                            }}
                                                        />
                                                        {a.automationType === 0 && (
                                                            <Tooltip title={publishingIds.has(a.id) ? 'Yayınlanıyor...' : 'Yayınla'}>
                                                                <IconButton
                                                                    size="small"
                                                                    disabled={publishingIds.has(a.id)}
                                                                    onClick={async (e) => {
                                                                        e.stopPropagation();
                                                                        const next = new Set(publishingIds);
                                                                        next.add(a.id);
                                                                        setPublishingIds(next);
                                                                        try {
                                                                            await publishAnnouncementAction(a.id);
                                                                            // Refresh current page
                                                                            const { data, meta } = await fetchAnnouncements(params);
                                                                            setItems(Array.isArray(data) ? data : []);
                                                                            setTotalPages(meta?.totalPages || 1);
                                                                        } catch (err) {
                                                                            // no-op, could show toast
                                                                        } finally {
                                                                            setPublishingIds((prev) => {
                                                                                const copy = new Set(prev);
                                                                                copy.delete(a.id);
                                                                                return copy;
                                                                            });
                                                                        }
                                                                    }}
                                                                    sx={{
                                                                        backgroundColor: 'transparent',
                                                                        border: '1px solid #0E4D94',
                                                                        borderRadius: '50%',
                                                                        width: 24,
                                                                        height: 24,
                                                                        '&:hover': {
                                                                            backgroundColor: '#E7EDF4',
                                                                        },
                                                                        '&:disabled': {
                                                                            backgroundColor: 'transparent',
                                                                            borderColor: '#a3a3a3',
                                                                        },
                                                                        '& svg': {
                                                                            fontSize: '12px',
                                                                            color: '#0E4D94',
                                                                        },
                                                                    }}
                                                                >
                                                                    {publishingIds.has(a.id) ? <CircularProgress size={12} sx={{ color: '#0E4D94' }} /> : <PublishIcon />}
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Stack>
                                                )}
                                            </TableCell>
                                            <TableCell>{formatDate(a.publishAt)}</TableCell>
                                            <TableCell>{formatDate(a.createdAt)}</TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <Tooltip title="Düzenle">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedAnnouncementId(a.id);
                                                                setEditOpen(true);
                                                            }}
                                                            aria-label="Düzenle"
                                                            sx={{
                                                                backgroundColor: '#E7EDF4',
                                                                borderRadius: '6px',
                                                                width: 32,
                                                                height: 32,
                                                                '&:hover': {
                                                                    backgroundColor: '#CFDBEA',
                                                                },
                                                                '& svg': {
                                                                    fontSize: '16px',
                                                                    color: '#0E4D94',
                                                                },
                                                            }}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Sil">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelected(a);
                                                                setConfirmOpen(true);
                                                            }}
                                                            aria-label="Sil"
                                                            sx={{
                                                                backgroundColor: '#FFEEF3',
                                                                borderRadius: '6px',
                                                                width: 32,
                                                                height: 32,
                                                                '&:hover': {
                                                                    backgroundColor: '#FFD6E0',
                                                                },
                                                                '& svg': {
                                                                    fontSize: '16px',
                                                                    color: '#F8285A',
                                                                },
                                                            }}
                                                        >
                                                            <DeleteOutlineIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} />
                    </Box>
                </>
            )}

            <CreateAnnouncementDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onSuccess={handleCreateSuccess}
            />

            <EditAnnouncementDialog
                open={editOpen}
                onClose={() => {
                    setEditOpen(false);
                    setSelectedAnnouncementId(null);
                }}
                announcementId={selectedAnnouncementId}
                onSaved={async () => {
                    setEditOpen(false);
                    setSelectedAnnouncementId(null);
                    // Refresh list
                    try {
                        const { data, meta } = await fetchAnnouncements(params);
                        setItems(Array.isArray(data) ? data : []);
                        setTotalPages(meta?.totalPages || 1);
                    } catch (e) {
                        console.error('Refresh error:', e);
                    }
                }}
            />

            {/* Delete confirm */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle
                    sx={{
                        fontSize: '18px',
                        fontWeight: 500,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pr: 3,
                    }}
                >
                    Duyuruyu Sil
                    <IconButton
                        onClick={() => setConfirmOpen(false)}
                        size="small"
                        sx={{ color: 'text.secondary', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ py: 2, px: 3 }}>
                    <DialogContentText>
                        Bu duyuruyu silmek istediğinizden emin misiniz?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ py: 2, px: 3 }}>
                    <Button
                        onClick={() => setConfirmOpen(false)}
                        sx={{
                            textTransform: 'none',
                            color: '#0E4D94',
                        }}
                    >
                        İptal
                    </Button>
                    <Button
                        variant="contained"
                        onClick={async () => {
                            if (!selected) return;
                            try {
                                await deleteAnnouncementAction(selected.id);
                                setConfirmOpen(false);
                                setSelected(null);
                                // refresh list
                                if (page === 1) {
                                    try {
                                        const params = {
                                            pageNumber: page,
                                            pageSize: pageSize,
                                            ...(search && { search }),
                                            ...(isPublished !== '' && { isPublished: isPublished === 'true' }),
                                            ...(category !== '' && { category: Number(category) }),
                                        };
                                        const { data, meta } = await fetchAnnouncements(params);
                                        setItems(Array.isArray(data) ? data : []);
                                        setTotalPages(meta?.totalPages || 1);
                                    } catch { }
                                } else {
                                    setPage(1);
                                }
                            } catch (e) {
                                console.error('Delete error:', e);
                            }
                        }}
                        sx={{
                            borderRadius: '5px',
                            textTransform: 'none',
                            fontSize: '14px',
                            boxShadow: 'none',
                            backgroundColor: '#F8285A',
                            '&:hover': {
                                boxShadow: 'none',
                                backgroundColor: '#E01E4A',
                            },
                        }}
                    >
                        Sil
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Announcements;



